
import { createContext, useEffect, useState } from "react";
import { Snackbar, Alert, Slide, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { setSessionExpiredCallback } from '../api/axios';

export const ErrorContext = createContext();

export const ErrorProvider = ({ children }) => {
    const [error, setError] = useState(null);
    const [severity, setSeverity] = useState('error');
    const [sessionExpired, setSessionExpired] = useState(false);

    // Register the session expired callback with axios
    useEffect(() => {
        setSessionExpiredCallback(() => {
            setSessionExpired(true);
        });
    }, []);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setError(null);
        setSeverity('error'); // Reset severity when closing
    };

    const handleSessionExpiredClose = () => {
        setSessionExpired(false);
        // Clear the session storage flag
        sessionStorage.removeItem('shown403');
        // Redirect to login with current path as redirect parameter
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/auth/sign-in?redirect=${encodeURIComponent(currentPath)}`;
    };

    // Function to trigger session expired modal
    const triggerSessionExpired = () => {
        setSessionExpired(true);
    };

    // Determine if the message is a loading message
    const isLoadingMessage = (message) => {
        return message && (message.toLowerCase().includes('loading') ||
                          message.toLowerCase().includes('analyzing') ||
                          message.toLowerCase().includes('processing'));
    };

    // Determine if the message is a success message
    const isSuccessMessage = (message) => {
        return message && (message.toLowerCase().includes('successfully') ||
                          message.toLowerCase().includes('uploaded') ||
                          message.toLowerCase().includes('complete') ||
                          message.toLowerCase().includes('checking status') ||
                          (message.toLowerCase().includes('processing') && 
                           message.toLowerCase().includes('checking')));
    };

    // Get the appropriate severity based on message content
    const getSeverity = (message) => {
        if (!message) return severity; // Use stored severity if message is null
        if (isSuccessMessage(message)) return 'success';
        return isLoadingMessage(message) ? 'warning' : 'error';
    };

    // Update severity when error changes
    useEffect(() => {
        if (error) {
            if (isSuccessMessage(error)) {
                setSeverity('success');
            } else {
                setSeverity(isLoadingMessage(error) ? 'warning' : 'error');
            }
        }
    }, [error]);

    return (<ErrorContext.Provider value={{ setError, triggerSessionExpired }}>
        {/* Session Expired Modal */}
        <Dialog
            open={sessionExpired}
            onClose={handleSessionExpiredClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                        Session Expired
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Your session has timed out due to inactivity. For your security, please log in again to continue.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    You'll be redirected to the login page and brought back to where you left off.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSessionExpiredClose}
                    fullWidth
                    sx={{
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                    }}
                >
                    Go to Login
                </Button>
            </DialogActions>
        </Dialog>

        {/* Error Snackbar */}
        <Snackbar
            open={!!error}
            autoHideDuration={4000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            slots={{ transition: Slide }}
        >
            <Alert
                onClose={handleClose}
                severity={getSeverity(error)}
                variant="filled"
                sx={{
                    width: '100%',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '& .MuiAlert-icon': {
                        fontSize: '1.2rem'
                    }
                }}
            >
                {error}
            </Alert>
        </Snackbar>
        {children}
    </ErrorContext.Provider>)

};
